import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Skill, SkillBranch } from '@/types';

interface SkillTreeProps {
  skills: Skill[];
  characterLevel: number;
  onClose: () => void;
}

const BRANCH_INFO: Record<SkillBranch, { name: string; emoji: string; color: string; gradient: string }> = {
  mind: { 
    name: 'Разум', 
    emoji: '🧠', 
    color: 'text-mind',
    gradient: 'from-mind/30 to-mind/10',
  },
  body: { 
    name: 'Тело', 
    emoji: '💪', 
    color: 'text-body',
    gradient: 'from-body/30 to-body/10',
  },
  connections: { 
    name: 'Связи', 
    emoji: '🤝', 
    color: 'text-connections',
    gradient: 'from-connections/30 to-connections/10',
  },
  meaning: { 
    name: 'Смыслы', 
    emoji: '✨', 
    color: 'text-meaning',
    gradient: 'from-meaning/30 to-meaning/10',
  },
};

export function SkillTree({ skills, characterLevel, onClose }: SkillTreeProps) {
  const [selectedBranch, setSelectedBranch] = useState<SkillBranch>('mind');
  const [selectedSkill, setSelectedSkill] = useState<Skill | null>(null);
  
  // Группируем навыки по веткам
  const groupedSkills = skills.reduce((acc, skill) => {
    if (!acc[skill.branch]) {
      acc[skill.branch] = [];
    }
    acc[skill.branch].push(skill);
    return acc;
  }, {} as Record<SkillBranch, Skill[]>);
  
  const branchSkills = groupedSkills[selectedBranch] || [];
  const branchInfo = BRANCH_INFO[selectedBranch];
  
  return (
    <div className="min-h-screen bg-primary-950 flex flex-col safe-area-top safe-area-bottom">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-primary-800/50">
        <button onClick={onClose} className="text-primary-400">
          ← Назад
        </button>
        <h1 className="text-lg font-semibold text-white">Дерево навыков</h1>
        <div className="w-16 text-right text-sm text-primary-400">
          Ур. {characterLevel}
        </div>
      </div>
      
      {/* Branch Tabs */}
      <div className="flex border-b border-primary-800/50">
        {(Object.keys(BRANCH_INFO) as SkillBranch[]).map(branch => {
          const info = BRANCH_INFO[branch];
          const isActive = branch === selectedBranch;
          
          return (
            <button
              key={branch}
              onClick={() => setSelectedBranch(branch)}
              className={`flex-1 py-3 flex flex-col items-center gap-1 transition-colors ${
                isActive 
                  ? `bg-primary-800/50 ${info.color}` 
                  : 'text-primary-500'
              }`}
            >
              <span className="text-xl">{info.emoji}</span>
              <span className="text-xs">{info.name}</span>
            </button>
          );
        })}
      </div>
      
      {/* Skills Grid */}
      <div className="flex-1 overflow-y-auto p-4">
        <motion.div
          key={selectedBranch}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          {branchSkills.map((skill, index) => (
            <SkillCard
              key={skill.skillKey}
              skill={skill}
              branchInfo={branchInfo}
              index={index}
              onClick={() => setSelectedSkill(skill)}
            />
          ))}
          
          {branchSkills.length === 0 && (
            <div className="text-center py-12 text-primary-500">
              Навыки пока недоступны
            </div>
          )}
        </motion.div>
      </div>
      
      {/* Skill Detail Modal */}
      <AnimatePresence>
        {selectedSkill && (
          <SkillDetailModal
            skill={selectedSkill}
            branchInfo={BRANCH_INFO[selectedSkill.branch]}
            onClose={() => setSelectedSkill(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

interface SkillCardProps {
  skill: Skill;
  branchInfo: typeof BRANCH_INFO[SkillBranch];
  index: number;
  onClick: () => void;
}

function SkillCard({ skill, branchInfo, index, onClick }: SkillCardProps) {
  const progress = skill.xpToNextLevel > 0 
    ? (skill.currentXp / skill.xpToNextLevel) * 100 
    : 0;
  
  const isMaxed = skill.currentLevel >= 10;
  const isLocked = !skill.unlocked;
  
  return (
    <motion.button
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={onClick}
      disabled={isLocked}
      className={`w-full p-4 rounded-xl text-left transition-all ${
        isLocked
          ? 'bg-primary-900/30 opacity-50'
          : 'bg-primary-800/50 hover:bg-primary-800/70'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${branchInfo.gradient} flex items-center justify-center`}>
            {isLocked ? '🔒' : branchInfo.emoji}
          </div>
          <div>
            <p className="font-medium text-white">{skill.skillName}</p>
            <p className="text-xs text-primary-400">
              {isLocked ? 'Заблокировано' : isMaxed ? 'Максимум' : `Уровень ${skill.currentLevel}/10`}
            </p>
          </div>
        </div>
        
        {!isLocked && !isMaxed && (
          <div className="text-xs text-primary-500">
            {skill.currentXp}/{skill.xpToNextLevel} XP
          </div>
        )}
      </div>
      
      {/* Progress bar */}
      {!isLocked && !isMaxed && (
        <div className="h-1.5 bg-primary-700 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className={`h-full rounded-full ${
              skill.branch === 'mind' ? 'bg-mind' :
              skill.branch === 'body' ? 'bg-body' :
              skill.branch === 'connections' ? 'bg-connections' :
              'bg-meaning'
            }`}
          />
        </div>
      )}
      
      {/* Level dots */}
      {!isLocked && (
        <div className="flex gap-1 mt-2">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className={`w-2 h-2 rounded-full ${
                i < skill.currentLevel 
                  ? skill.branch === 'mind' ? 'bg-mind' :
                    skill.branch === 'body' ? 'bg-body' :
                    skill.branch === 'connections' ? 'bg-connections' :
                    'bg-meaning'
                  : 'bg-primary-700'
              }`}
            />
          ))}
        </div>
      )}
    </motion.button>
  );
}

interface SkillDetailModalProps {
  skill: Skill;
  branchInfo: typeof BRANCH_INFO[SkillBranch];
  onClose: () => void;
}

function SkillDetailModal({ skill, branchInfo, onClose }: SkillDetailModalProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-primary-900 rounded-t-3xl p-6 w-full max-w-lg safe-area-bottom"
      >
        {/* Handle */}
        <div className="w-12 h-1 bg-primary-700 rounded-full mx-auto mb-6" />
        
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${branchInfo.gradient} flex items-center justify-center`}>
            <span className="text-3xl">{branchInfo.emoji}</span>
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{skill.skillName}</h2>
            <p className={`text-sm ${branchInfo.color}`}>
              {branchInfo.name} • Уровень {skill.currentLevel}/10
            </p>
          </div>
        </div>
        
        {/* Description */}
        {skill.description && (
          <p className="text-primary-300 mb-6">{skill.description}</p>
        )}
        
        {/* Progress */}
        <div className="bg-primary-800/50 rounded-xl p-4 mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-primary-400">Прогресс до следующего уровня</span>
            <span className="text-primary-300">{skill.currentXp}/{skill.xpToNextLevel} XP</span>
          </div>
          <div className="h-3 bg-primary-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                skill.branch === 'mind' ? 'bg-mind' :
                skill.branch === 'body' ? 'bg-body' :
                skill.branch === 'connections' ? 'bg-connections' :
                'bg-meaning'
              }`}
              style={{ width: `${(skill.currentXp / skill.xpToNextLevel) * 100}%` }}
            />
          </div>
        </div>
        
        {/* Level visualization */}
        <div className="flex justify-center gap-2 mb-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <div
              key={i}
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                i < skill.currentLevel
                  ? `bg-gradient-to-br ${branchInfo.gradient} text-white`
                  : 'bg-primary-800 text-primary-600'
              }`}
            >
              {i + 1}
            </div>
          ))}
        </div>
        
        {/* How to level up */}
        <div className="bg-primary-800/30 rounded-xl p-4">
          <p className="text-sm text-primary-400 mb-2">Как прокачать:</p>
          <ul className="text-sm text-primary-300 space-y-1">
            {skill.branch === 'mind' && (
              <>
                <li>• Медитации (+15 XP)</li>
                <li>• Рефлексии (+10 XP)</li>
              </>
            )}
            {skill.branch === 'body' && (
              <>
                <li>• Дыхательные практики (+15 XP)</li>
                <li>• Медитации (+5 XP)</li>
              </>
            )}
            {skill.branch === 'connections' && (
              <>
                <li>• Посещение встреч (+30 XP)</li>
                <li>• Помощь новичкам (+20 XP)</li>
              </>
            )}
            {skill.branch === 'meaning' && (
              <>
                <li>• Глубокие рефлексии (+25 XP)</li>
                <li>• Сюжетные квесты (+50 XP)</li>
              </>
            )}
          </ul>
        </div>
      </motion.div>
    </motion.div>
  );
}
